import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { scrollToElement } from '../../utils/scrollUtils';
import { useContactInfo } from '../../utils/contactObfuscation';
import { headerSocialLinks } from '../../data/socialLinks';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileDropdown, setOpenMobileDropdown] = useState(null);
  const [openDesktopDropdown, setOpenDesktopDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const contactInfo = useContactInfo();

  const navigationItems = [
    { 
      label: 'HOME', 
      path: '/', 
      icon: 'Home'
    },
    { 
      label: 'PRODUCTS', 
      path: '/product', 
      icon: 'Package',
      hasDropdown: true,
      dropdownItems: [
        {
          title: 'Email Validator SaaS',
          description: 'Professional email validation service with 99.9% accuracy. Validate single emails or bulk lists to reduce bounce rates.',
          link: 'https://mailvalidator.octalchip.com/',
          icon: 'Mail'
        },
        {
          title: 'QuickSite',
          description: 'Done-for-you business website with hosting, SSL, templates, and WhatsApp-led domain setup with a clear fast launch plan.',
          link: 'https://quicksite.octalchip.com/',
          icon: 'LayoutTemplate'
        }
      ]
    },
    { 
      label: 'SERVICES', 
      path: '/services', 
      icon: 'Settings',
      hasDropdown: true,
      dropdownItems: [
        {
          title: 'Web Development',
          description: 'Still losing leads on slow pages? Build fast web apps that convert.',
          link: '/services/web-development',
          icon: 'Globe'
        },
        {
          title: 'Mobile App Development',
          description: 'Launch mobile apps that improve retention, engagement, and revenue.',
          link: '/services/mobile-app-development',
          icon: 'Smartphone'
        },
        {
          title: 'AI Integration',
          description: 'Automate decisions and reduce manual work with practical AI integrations.',
          link: '/services/ai-integration',
          icon: 'Cpu'
        },
        {
          title: 'Cloud & DevOps',
          description: 'Scale reliably with cloud architecture, CI/CD, and lower operational risk.',
          link: '/services/cloud-devops',
          icon: 'Cloud'
        },
        {
          title: 'UI/UX Design',
          description: 'Turn user friction into higher conversions with UX-led product design.',
          link: '/services/ui-ux-design',
          icon: 'LayoutTemplate'
        },
        {
          title: 'Backend Development',
          description: 'Build secure APIs and backend systems that scale with demand.',
          link: '/services/backend-development',
          icon: 'Server'
        },
        {
          title: 'Workflow Automation',
          description: 'Stop repetitive manual tasks with n8n, Zapier, and Make automation.',
          link: '/services/workflow-automation',
          icon: 'Settings'
        },
        {
          title: 'Marketing Services',
          description: 'Scale pipeline faster with WhatsApp, LinkedIn, RCS, and email automation.',
          link: '/services/marketing-services',
          icon: 'Megaphone'
        }
      ]
    },
    { 
      label: 'AI/ML', 
      path: '/ai-ml-services', 
      icon: 'Brain',
      hasDropdown: true,
      dropdownItems: [
        {
          title: 'Machine Learning',
          description: 'Custom ML models that improve forecasting and automated decision quality.',
          link: '/services/ai-ml-services/machine-learning',
          icon: 'Brain'
        },
        {
          title: 'Natural Language Processing',
          description: 'Turn support chats and documents into decisions with production NLP.',
          link: '/services/ai-ml-services/natural-language-processing',
          icon: 'FileText'
        },
        {
          title: 'Computer Vision',
          description: 'Detect defects, track activity, and automate visual checks in real time.',
          link: '/services/ai-ml-services/computer-vision',
          icon: 'Eye'
        },
        {
          title: 'Predictive Analytics',
          description: 'Forecast demand and risk faster to protect margin and growth.',
          link: '/services/ai-ml-services/predictive-analytics',
          icon: 'TrendingUp'
        },
        {
          title: 'AI Chatbots',
          description: 'Resolve customer queries instantly and cut support workload.',
          link: '/services/ai-ml-services/ai-chatbots',
          icon: 'MessageCircle'
        },
        {
          title: 'Deep Learning',
          description: 'Use deep models where classic ML falls short on complex patterns.',
          link: '/services/ai-ml-services/deep-learning',
          icon: 'Network'
        },
        {
          title: 'Data Science',
          description: 'Turn raw data into clear actions that improve revenue efficiency.',
          link: '/services/ai-ml-services/data-science',
          icon: 'BarChart'
        },
        {
          title: 'AI Consulting',
          description: 'Turn AI ideas into a clear roadmap with ROI-focused implementation strategy.',
          link: '/services/ai-ml-services/ai-consulting',
          icon: 'Lightbulb'
        },
        {
          title: 'Reinforcement Learning',
          description: 'Train autonomous agents to optimize decisions in dynamic systems.',
          link: '/services/ai-ml-services/reinforcement-learning',
          icon: 'Network'
        }
      ]
    },
    { 
      label: 'BLOG', 
      path: '/blog', 
      icon: 'BookOpen'
    },
    { 
      label: 'HIRE DEVELOPERS', 
      path: '/services#pricing', 
      icon: 'Users',
      hasDropdown: true,
      dropdownItems: [
        {
          title: 'Hourly Engagement',
          description: 'Scale delivery this week with flexible experts when deadlines are tight.',
          link: '/services#pricing'
        },
        {
          title: 'Monthly Dedicated',
          description: 'Get a dedicated team that ships consistently and reduces hiring delay.',
          link: '/services#pricing'
        },
        {
          title: 'Fixed Cost Projects',
          description: 'Lock scope, budget, and timeline for predictable project outcomes.',
          link: '/services#pricing'
        }
      ]
    },
    { 
      label: 'ABOUT', 
      path: '/about', 
      icon: 'Users'
    },
    { 
      label: 'CONTACT', 
      path: '/contact', 
      icon: 'Mail'
    }
  ];

  const isActiveRoute = (path) => {
    // If path has a hash, check both pathname and hash
    if (path.includes('#')) {
      const [pathWithoutHash, hash] = path.split('#');
      return location.pathname === pathWithoutHash && location.hash === `#${hash}`;
    }
    // If path doesn't have a hash, only check pathname (ignore hash in URL)
    return location.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setOpenMobileDropdown(null);
  };

  const toggleMobileDropdown = (itemLabel) => {
    setOpenMobileDropdown(openMobileDropdown === itemLabel ? null : itemLabel);
  };

  const scrollToSection = async (sectionId) => {
    await scrollToElement(sectionId, { 
      smooth: true,
      maxRetries: 15,
      retryDelay: 150
    }).catch(() => {
      // Silent fail - scrollToElement handles retries internally
    });
    setIsMobileMenuOpen(false);
    setOpenMobileDropdown(null);
    setOpenDesktopDropdown(null);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDesktopDropdown && !event.target.closest('.relative.group')) {
        setOpenDesktopDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [openDesktopDropdown]);

  return (
    <header className="bg-surface border-b border-border fixed top-[60px] sm:top-[65px] md:top-[70px] lg:top-[80px] w-full z-[1001] shadow-sm">
      {/* Top Bar with Contact Info and Social Media */}
      <div className="bg-[#00d690] text-white py-2.5 sm:py-3 md:py-3 lg:py-4">
        <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 md:gap-5 lg:gap-6">
            {/* Contact Information */}
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 sm:gap-4 md:gap-5 lg:gap-6 text-sm sm:text-base flex-shrink-0">
              <div className="flex items-center space-x-2 sm:space-x-2.5">
                <Icon name="Phone" size={18} className="text-white sm:w-5 sm:h-5 flex-shrink-0" />
                {contactInfo.phoneLink && (
                  <a href={contactInfo.phoneLink} className="hover:text-white/80 transition-colors font-medium whitespace-nowrap">
                    {contactInfo.phone}
                  </a>
                )}
              </div>
              {contactInfo.mailtoLink && (
                <div className="flex items-center space-x-2 sm:space-x-2.5">
                  <Icon name="Mail" size={18} className="text-white sm:w-5 sm:h-5 flex-shrink-0" />
                  <a href={contactInfo.mailtoLink} className="hover:text-white/80 transition-colors font-medium break-all sm:break-normal">
                    {contactInfo.email}
                  </a>
                </div>
              )}
            </div>

            {/* Social Media Icons - Top Platforms Only (Hidden on mobile) */}
            <div className="hidden sm:flex items-center justify-center sm:justify-end">
              <div className="flex items-center gap-2.5 sm:gap-3 md:gap-3.5 lg:gap-4">
                {headerSocialLinks.map((social, index) => (
                  <a 
                    key={index}
                    href={social.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 lg:w-11 lg:h-11 rounded-full text-white hover:text-white hover:bg-white/10 active:bg-white/20 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50 flex-shrink-0"
                    aria-label={social.name}
                  >
                    <Icon name={social.icon} size={18} className="sm:w-5 sm:h-5 md:w-5 md:h-5 lg:w-6 lg:h-6" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div className="bg-[#f4faf8] relative border-b border-gray-200">
        <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10 2xl:px-12 relative">
          <div className="flex items-center h-14 sm:h-16 md:h-[72px] lg:h-20 gap-2 sm:gap-3 md:gap-4">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0 min-w-0">
              <img 
                src="/octalchiplogo.svg" 
                alt="OctalChip - Software Development Company Logo - Web, Mobile, AI/ML Services" 
                className="h-5 sm:h-6 md:h-6 lg:h-7 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden xl:flex items-center space-x-6 2xl:space-x-8 flex-1 justify-center mx-4 lg:mx-8">
              {navigationItems.map((item) => (
                <div 
                  key={item.path} 
                  className="relative group flex items-center"
                  onMouseEnter={() => item.hasDropdown && setOpenDesktopDropdown(item.label)}
                  onMouseLeave={() => setOpenDesktopDropdown(null)}
                >
                  <Link
                    to={item.path}
                    onClick={(e) => {
                      if (item.path.startsWith('/#')) {
                        e.preventDefault();
                        scrollToSection(item.path.substring(2));
                      }
                      // If it has a dropdown, allow navigation to the page
                      // The dropdown will be controlled by the arrow button only
                    }}
                    className={`flex items-center space-x-1 text-base font-medium transition-all duration-200 pb-1 border-b-2 ${
                      isActiveRoute(item.path) 
                        ? 'text-[#00d690] border-[#00d690]' 
                        : 'text-gray-800 hover:text-[#00d690] border-transparent hover:border-[#00d690]/50'
                    }`}
                  >
                    <span>{item.label}</span>
                  </Link>
                  {item.hasDropdown && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setOpenDesktopDropdown(openDesktopDropdown === item.label ? null : item.label);
                      }}
                      className="ml-1 p-1 rounded-full hover:bg-gray-50 transition-colors flex items-center justify-center btn-animate-pulse"
                      aria-label="Toggle dropdown"
                    >
                      <Icon 
                        name={openDesktopDropdown === item.label ? 'ChevronUp' : 'ChevronDown'} 
                        size={14} 
                        className="transition-transform duration-200 text-gray-600 hover:text-[#00d690]" 
                      />
                    </button>
                  )}
                  
                  {/* Dropdown Menu */}
                  {item.hasDropdown && (
                    <div 
                      className={`${item.label === 'SERVICES' || item.label === 'AI/ML' ? 'fixed' : 'absolute top-full'} mt-1 bg-white border border-gray-200 rounded-lg shadow-2xl transition-all duration-200 overflow-hidden ${
                        openDesktopDropdown === item.label 
                          ? 'opacity-100 visible translate-y-0' 
                          : 'opacity-0 invisible -translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0'
                      } ${
                        item.label === 'SERVICES' || item.label === 'AI/ML' 
                          ? 'w-[90vw] max-w-[1000px] xl:w-[1000px] 2xl:w-[1200px]' 
                          : item.label === 'PRODUCTS' 
                          ? 'w-[90vw] max-w-[600px] xl:w-[600px] 2xl:w-[650px] left-[55%] lg:left-[60%] xl:left-[65%] 2xl:left-[70%]' 
                          : 'w-[90vw] max-w-[260px] lg:w-[260px] xl:w-[280px] left-1/2 -translate-x-1/2'
                      }`}
                      style={{ 
                        maxWidth: item.label === 'SERVICES' || item.label === 'AI/ML' 
                          ? 'min(90vw, 1200px)' 
                          : item.label === 'PRODUCTS' 
                          ? 'min(90vw, 650px)' 
                          : 'min(90vw, 280px)',
                        zIndex: 1002,
                        ...(item.label === 'SERVICES' || item.label === 'AI/ML' ? {
                          top: '208px', // 80px (top banner) + 128px (header height)
                          left: '50%',
                          transform: 'translateX(-50%)',
                          marginTop: '4px'
                        } : {})
                      }}
                      onMouseEnter={() => setOpenDesktopDropdown(item.label)}
                      onMouseLeave={() => setOpenDesktopDropdown(null)}
                    >
                      <div className="py-2 sm:py-3 lg:py-4 overflow-hidden">
                        {item.label === 'AI/ML' ? (
                          // AI/ML dropdown with responsive layout
                          <div className="grid grid-cols-1 lg:grid-cols-4 gap-0 overflow-hidden">
                            {/* First 2 columns: AI/ML Services */}
                            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-0 overflow-hidden">
                              {item.dropdownItems.map((dropdownItem, index) => (
                                <div key={index} className="px-3 sm:px-4 py-3 sm:py-4 hover:bg-[#00d690]/5 transition-colors border-b sm:border-b-0 sm:border-r border-gray-100 last:border-b-0 overflow-hidden">
                                  <div className="flex flex-col sm:flex-row items-start sm:items-start space-y-2 sm:space-y-0 sm:space-x-3 min-w-0">
                                    <div className="flex-shrink-0">
                                      <Icon name={dropdownItem.icon} size={20} className="text-[#00d690]" />
                                    </div>
                                    <div className="flex-1 min-w-0 w-full overflow-hidden">
                                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-1 min-w-0">
                                        <h4 className="text-sm sm:text-base font-semibold text-gray-900 break-words min-w-0">
                                          {dropdownItem.title}
                                        </h4>
                                        {dropdownItem.link.startsWith('http') ? (
                                          <a 
                                            href={dropdownItem.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs sm:text-sm text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-2 sm:px-3 py-1 rounded-full border border-[#00d690] hover:bg-[#00d690] hover:text-white whitespace-nowrap inline-block flex-shrink-0 btn-animate-wiggle"
                                          >
                                            View →
                                          </a>
                                        ) : (
                                          <Link 
                                            to={dropdownItem.link}
                                            className="text-xs sm:text-sm text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-2 sm:px-3 py-1 rounded-full border border-[#00d690] hover:bg-[#00d690] hover:text-white whitespace-nowrap inline-block flex-shrink-0 btn-animate-wiggle"
                                          >
                                            View →
                                          </Link>
                                        )}
                                      </div>
                                      <p className="text-xs sm:text-sm text-gray-600 leading-relaxed break-words">
                                        {dropdownItem.description}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Last 2 columns: CTA */}
                            <div className="lg:col-span-2 bg-[#f4faf8] px-4 sm:px-6 py-6 sm:py-8 flex flex-col justify-center border-t lg:border-t-0 lg:border-l border-gray-200">
                              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                                Turn AI Plans Into Growth
                              </h3>
                              <p className="text-sm text-gray-600 mb-4 sm:mb-5 leading-relaxed">
                                Get a practical 30-minute roadmap call focused on outcomes, ROI, and speed to launch.
                              </p>
                              <a
                                href="https://cal.com/octalchip/30min"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-[#00d690] hover:bg-[#00b876] text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold text-sm sm:text-base transition-all duration-300 inline-flex items-center justify-center w-fit shadow-md hover:shadow-lg btn-animate-scale"
                              >
                                Book Free Call
                                <Icon name="ChevronRight" size={16} className="ml-2" />
                              </a>
                            </div>
                          </div>
                        ) : item.label === 'SERVICES' ? (
                          // Services dropdown with responsive layout
                          <div className="grid grid-cols-1 lg:grid-cols-4 gap-0 overflow-hidden">
                            {/* First 2 columns: Services */}
                            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-0 overflow-hidden">
                              {item.dropdownItems.map((dropdownItem, index) => (
                                <div key={index} className="px-3 sm:px-4 py-3 sm:py-4 hover:bg-[#00d690]/5 transition-colors border-b sm:border-b-0 sm:border-r border-gray-100 last:border-b-0 overflow-hidden">
                                  <div className="flex flex-col sm:flex-row items-start sm:items-start space-y-2 sm:space-y-0 sm:space-x-3 min-w-0">
                                    <div className="flex-shrink-0">
                                      <Icon name={dropdownItem.icon} size={20} className="text-[#00d690]" />
                                    </div>
                                    <div className="flex-1 min-w-0 w-full overflow-hidden">
                                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-1 min-w-0">
                                        <h4 className="text-sm sm:text-base font-semibold text-gray-900 break-words min-w-0">
                                          {dropdownItem.title}
                                        </h4>
                                        {dropdownItem.link.startsWith('http') ? (
                                          <a 
                                            href={dropdownItem.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs sm:text-sm text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-2 sm:px-3 py-1 rounded-full border border-[#00d690] hover:bg-[#00d690] hover:text-white whitespace-nowrap inline-block flex-shrink-0 btn-animate-wiggle"
                                          >
                                            View →
                                          </a>
                                        ) : (
                                          <Link 
                                            to={dropdownItem.link}
                                            className="text-xs sm:text-sm text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-2 sm:px-3 py-1 rounded-full border border-[#00d690] hover:bg-[#00d690] hover:text-white whitespace-nowrap inline-block flex-shrink-0 btn-animate-wiggle"
                                          >
                                            View →
                                          </Link>
                                        )}
                                      </div>
                                      <p className="text-xs sm:text-sm text-gray-600 leading-relaxed break-words">
                                        {dropdownItem.description}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Last 2 columns: CTA */}
                            <div className="lg:col-span-2 bg-[#f4faf8] px-4 sm:px-6 py-6 sm:py-8 flex flex-col justify-center border-t lg:border-t-0 lg:border-l border-gray-200">
                              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                                Need Faster Execution?
                              </h3>
                              <p className="text-sm text-gray-600 mb-4 sm:mb-5 leading-relaxed">
                                Book a free strategy call and get a practical plan to ship faster and convert more.
                              </p>
                              <a
                                href="https://cal.com/octalchip/30min"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-[#00d690] hover:bg-[#00b876] text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold text-sm sm:text-base transition-all duration-300 inline-flex items-center justify-center w-fit shadow-md hover:shadow-lg btn-animate-glow"
                              >
                                Book Free Call
                                <Icon name="ChevronRight" size={16} className="ml-2" />
                              </a>
                            </div>
                          </div>
                        ) : item.label === 'PRODUCTS' ? (
                          // Product dropdown with responsive layout
                          <div className="grid grid-cols-1 lg:grid-cols-4 gap-0">
                            {/* First 2 columns: Products */}
                            <div className="lg:col-span-2">
                              {item.dropdownItems.map((dropdownItem, index) => (
                                <div key={index} className="px-4 sm:px-5 py-3 sm:py-4 hover:bg-[#00d690]/5 transition-colors border-b border-gray-100 last:border-b-0">
                                  <div className="flex flex-col sm:flex-row items-start sm:items-start space-y-2 sm:space-y-0 sm:space-x-3">
                                    <div className="flex-shrink-0">
                                      <Icon name={dropdownItem.icon} size={20} className="text-[#00d690]" />
                                    </div>
                                    <div className="flex-1 min-w-0 w-full">
                                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-1">
                                        <h4 className="text-sm sm:text-base font-semibold text-gray-900">
                                          {dropdownItem.title}
                                        </h4>
                                        {dropdownItem.link.startsWith('http') ? (
                                          <a 
                                            href={dropdownItem.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs sm:text-sm text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-2 sm:px-3 py-1 rounded-full border border-[#00d690] hover:bg-[#00d690] hover:text-white whitespace-nowrap inline-block btn-animate-rotate"
                                          >
                                            View →
                                          </a>
                                        ) : (
                                          <Link 
                                            to={dropdownItem.link}
                                            className="text-xs sm:text-sm text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-2 sm:px-3 py-1 rounded-full border border-[#00d690] hover:bg-[#00d690] hover:text-white whitespace-nowrap inline-block btn-animate-rotate"
                                          >
                                            View →
                                          </Link>
                                        )}
                                      </div>
                                      <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                                        {dropdownItem.description}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Last 2 columns: CTA */}
                            <div className="lg:col-span-2 bg-[#f4faf8] px-4 sm:px-6 py-6 sm:py-8 flex flex-col justify-center border-t lg:border-t-0 lg:border-l border-gray-200">
                              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                                Explore Conversion Tools
                              </h3>
                              <p className="text-sm text-gray-600 mb-4 sm:mb-5 leading-relaxed">
                                Launch faster with products built to reduce friction and increase qualified leads.
                              </p>
                              <a
                                href="https://cal.com/octalchip/30min"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-[#00d690] hover:bg-[#00b876] text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold text-sm sm:text-base transition-all duration-300 inline-flex items-center justify-center w-fit shadow-md hover:shadow-lg btn-animate-float"
                              >
                                Book Free Call
                                <Icon name="ChevronRight" size={16} className="ml-2" />
                              </a>
                            </div>
                          </div>
                        ) : (
                          // Other dropdowns (Hire Developers, etc.)
                          <>
                            {item.dropdownItems.map((dropdownItem, index) => (
                              <div key={index} className="px-4 sm:px-5 py-3 sm:py-4 hover:bg-[#00d690]/5 transition-colors border-b border-gray-100 last:border-b-0">
                                {dropdownItem.link.startsWith('http') ? (
                                  <a 
                                    href={dropdownItem.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block text-sm sm:text-base text-gray-700 hover:text-[#00d690] transition-colors font-medium"
                                  >
                                    {dropdownItem.title}
                                  </a>
                                ) : (
                                  <Link 
                                    to={dropdownItem.link}
                                    className="block text-sm sm:text-base text-gray-700 hover:text-[#00d690] transition-colors font-medium"
                                  >
                                    {dropdownItem.title}
                                  </Link>
                                )}
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Right Side: CTA Button + Mobile Menu */}
            <div className="flex items-center gap-2 sm:gap-3 ml-auto">
              {/* CTA Button - Visible on all screens */}
              <div className="flex-shrink-0 flex items-center">
                <Button
                  variant="outline"
                  size="sm"
                  animation="glow"
                  onClick={() => window.open('https://cal.com/octalchip/30min', '_blank')}
                  className="border-primary text-primary bg-white hover:bg-primary hover:text-white transition-all duration-200 shadow-sm hover:shadow-md min-h-[36px] sm:min-h-[40px] md:min-h-[44px] active:scale-95"
                >
                  <span className="hidden sm:inline text-xs sm:text-sm md:text-base font-semibold px-2 sm:px-3 md:px-4 lg:px-5 whitespace-nowrap">
                    BOOK FREE CALL
                  </span>
                  <span className="sm:hidden text-xs font-semibold px-2.5 whitespace-nowrap">
                    FREE CALL
                  </span>
                </Button>
              </div>

              {/* Mobile Menu Button */}
              <button
                className="xl:hidden flex-shrink-0 p-2 sm:p-2.5 rounded-full btn-animate-bounce hover:bg-gray-100 active:bg-gray-200 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                onClick={toggleMobileMenu}
                aria-label="Toggle menu"
              >
                <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} className="text-gray-800 sm:w-6 sm:h-6" />
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <div className="xl:hidden border-t border-gray-200 bg-white max-h-[calc(100vh-140px)] overflow-y-auto">
              <nav className="py-2 sm:py-3 space-y-0.5 px-2 sm:px-3">
                {navigationItems.map((item) => (
                  <div key={item.path}>
                    <div className="flex items-center">
                      <button
                        onClick={() => {
                          if (item.path.startsWith('/#')) {
                            scrollToSection(item.path.substring(2));
                          } else if (item.path.includes('#')) {
                            // Handle paths with hash (e.g., /services#pricing)
                            const [path, hash] = item.path.split('#');
                            navigate(path);
                            setIsMobileMenuOpen(false);
                            // Scroll to section after navigation
                            setTimeout(() => {
                              scrollToSection(hash);
                            }, 100);
                          } else {
                            // Navigate to the page
                            if (item.path.startsWith('http')) {
                              window.open(item.path, '_blank');
                            } else {
                              navigate(item.path);
                            }
                            setIsMobileMenuOpen(false);
                          }
                        }}
                        className={`flex items-center space-x-3 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base font-medium rounded-full flex-1 text-left transition-all duration-200 border-l-4 btn-animate-scale ${
                          isActiveRoute(item.path)
                            ? 'text-[#00d690] bg-[#00d690]/5 border-[#00d690]' 
                            : 'text-gray-700 hover:text-[#00d690] hover:bg-[#00d690]/5 border-transparent hover:border-[#00d690]/50'
                        }`}
                      >
                        <Icon name={item.icon} size={18} className="sm:w-5 sm:h-5 flex-shrink-0" />
                        <span className="whitespace-normal leading-tight">{item.label}</span>
                      </button>
                      {item.hasDropdown && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleMobileDropdown(item.label);
                          }}
                          className="px-2 py-3 text-gray-700 hover:text-[#00d690] transition-colors rounded-full btn-animate-pulse"
                          aria-label="Toggle dropdown"
                        >
                          <Icon 
                            name={openMobileDropdown === item.label ? 'ChevronUp' : 'ChevronDown'} 
                            size={14} 
                          />
                        </button>
                      )}
                    </div>
                    
                    {/* Mobile Dropdown */}
                    {item.hasDropdown && openMobileDropdown === item.label && (
                      <div className="ml-8 sm:ml-10 mr-2 mt-1 bg-gray-50 rounded-md border border-gray-200">
                        {item.label === 'SERVICES' || item.label === 'AI/ML' ? (
                          // Services and AI/ML dropdown with simple single-column layout for mobile
                          <div className="px-2 sm:px-3 py-2">
                            {item.dropdownItems.map((dropdownItem, index) => (
                              <div key={index} className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200 last:border-b-0">
                                {dropdownItem.link.startsWith('http') ? (
                                  <a 
                                    href={dropdownItem.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block text-sm sm:text-base text-gray-700 hover:text-[#00d690] transition-colors font-medium"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    {dropdownItem.title}
                                  </a>
                                ) : (
                                  <Link 
                                    to={dropdownItem.link}
                                    className="block text-sm sm:text-base text-gray-700 hover:text-[#00d690] transition-colors font-medium"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    {dropdownItem.title}
                                  </Link>
                                )}
                              </div>
                            ))}
                            {item.label === 'SERVICES' && (
                              <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-t border-gray-200 bg-white/50">
                                <Link 
                                  to="/services"
                                  className="block text-sm sm:text-base font-semibold text-[#00d690] hover:text-[#00b876] transition-colors"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  View All Services →
                                </Link>
                              </div>
                            )}
                            {item.label === 'AI/ML' && (
                              <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-t border-gray-200 bg-white/50">
                                <Link 
                                  to="/ai-ml-services"
                                  className="block text-sm sm:text-base font-semibold text-[#00d690] hover:text-[#00b876] transition-colors"
                                  onClick={() => setIsMobileMenuOpen(false)}
                                >
                                  View All AI/ML Services →
                                </Link>
                              </div>
                            )}
                          </div>
                        ) : (
                          // Other dropdowns (Product, Hire Developers, etc.)
                          <>
                            {item.dropdownItems.map((dropdownItem, index) => (
                              <div key={index} className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200 last:border-b-0">
                                {(item.label === 'PRODUCTS') ? (
                                  // Product dropdown with description and icon
                                  <div className="flex items-start space-x-3">
                                    <div className="flex-shrink-0 mt-1">
                                      <Icon name={dropdownItem.icon} size={18} className="text-[#00d690] sm:w-5 sm:h-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                                        <h4 className="text-sm sm:text-base font-semibold text-gray-900">
                                          {dropdownItem.title}
                                        </h4>
                                        {dropdownItem.link.startsWith('http') ? (
                                          <a 
                                            href={dropdownItem.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs sm:text-sm text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-2 sm:px-3 py-1 rounded-full border border-[#00d690] hover:bg-[#00d690] hover:text-white whitespace-nowrap inline-block btn-animate-rotate"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                          >
                                            View →
                                          </a>
                                        ) : (
                                          <Link 
                                            to={dropdownItem.link}
                                            className="text-xs sm:text-sm text-[#00d690] hover:text-[#00b876] font-medium transition-colors px-2 sm:px-3 py-1 rounded-full border border-[#00d690] hover:bg-[#00d690] hover:text-white whitespace-nowrap inline-block btn-animate-rotate"
                                            onClick={() => setIsMobileMenuOpen(false)}
                                          >
                                            View →
                                          </Link>
                                        )}
                                      </div>
                                      <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                                        {dropdownItem.description}
                                      </p>
                                    </div>
                                  </div>
                                ) : (
                                  // Simple dropdown for other items
                                  <a 
                                    href={dropdownItem.link}
                                    className="block text-sm sm:text-base text-gray-700 hover:text-[#00d690] transition-colors font-medium"
                                    onClick={() => setIsMobileMenuOpen(false)}
                                  >
                                    {dropdownItem.title}
                                  </a>
                                )}
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Mobile CTA Button */}
                <div className="px-3 sm:px-4 py-3 border-t border-gray-200">
                  <Button
                    variant="outline"
                    size="md"
                    animation="glow"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      window.open('https://cal.com/octalchip/30min', '_blank');
                    }}
                    className="w-full border-primary text-primary bg-white hover:bg-primary hover:text-white whitespace-nowrap"
                  >
                    BOOK FREE CALL
                  </Button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;